import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./DomainPage.css";
import "../ServerPackage/CommonPage.css";
import Navbar from "../Common/Navbar";
import {
  connectRealtimeSocket,
  disconnectRealtimeSocket,
} from "../ServerPackage/CompleteCPanel/utils/realtimeSocket";

/* ==============================
   API BASE HELPER
============================== */
const normalizeBase = (value) => String(value || "").trim().replace(/\/+$/, "");

const ensureApiBase = (value) => {
  const clean = normalizeBase(value);

  if (!clean) return "http://localhost:5000/api";

  return clean.endsWith("/api") ? clean : `${clean}/api`;
};

const API_BASE = ensureApiBase(
  process.env.REACT_APP_API_BASE || "http://localhost:5000"
);

/* ==============================
   AUTH STORAGE KEYS
============================== */
const AUTH_KEYS = [
  "token",
  "authToken",
  "accessToken",
  "userToken",
  "jwtToken",
  "isLoggedIn",
  "user",
  "email",
  "userEmail",
  "user_email",
  "currentUser",
];

/* ==============================
   DOMAIN CONFIG
============================== */
const premiumTakenPrice = "₹16,999.00";

const extensionCards = [".com", ".net", ".org", ".ai", ".in", ".shop", ".tech"];

const defaultDnsRecord = {
  type: "A",
  host: "@",
  value: "",
  ttl: "3600",
  priority: 0,
};

/* ==============================
   AUTH HELPERS
============================== */
const getToken = () => {
  const tokenKeys = ["token", "authToken", "accessToken", "userToken", "jwtToken"];

  for (const key of tokenKeys) {
    const localValue = localStorage.getItem(key);
    const sessionValue = sessionStorage.getItem(key);

    if (localValue && String(localValue).trim()) {
      return String(localValue).trim();
    }

    if (sessionValue && String(sessionValue).trim()) {
      return String(sessionValue).trim();
    }
  }

  return "";
};

const clearAuth = () => {
  AUTH_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });

  disconnectRealtimeSocket();
  window.dispatchEvent(new Event("auth-state-changed"));
};

const getStoredUser = () => {
  const rawUser =
    localStorage.getItem("user") ||
    sessionStorage.getItem("user") ||
    localStorage.getItem("currentUser") ||
    sessionStorage.getItem("currentUser") ||
    "";

  if (!rawUser) return null;

  try {
    const parsed = JSON.parse(rawUser);

    if (!parsed || typeof parsed !== "object") return null;

    return parsed;
  } catch {
    return null;
  }
};

/* ==============================
   DOMAIN HELPERS
============================== */
const normalizeDomain = (value = "") => {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/.*$/, "")
    .replace(/\s+/g, "");
};

const getBaseName = (value = "") => {
  const clean = normalizeDomain(value);
  return clean.split(".")[0] || clean;
};

const getDomainExtension = (domainName = "") => {
  const clean = normalizeDomain(domainName);
  const parts = clean.split(".");

  if (parts.length < 2) return "";

  return `.${parts[parts.length - 1]}`;
};

const parsePriceToNumber = (value) => {
  if (typeof value === "number") return value;

  const clean = String(value || "")
    .replace(/[₹,\s]/g, "")
    .replace(/[^0-9.]/g, "");

  const amount = Number(clean);

  return Number.isFinite(amount) && amount > 0 ? amount : 0;
};

const getDomainPrice = (domain = {}) => {
  const rawAmount = Number(domain.rawPrice || 0);

  if (rawAmount > 0) return rawAmount;

  return parsePriceToNumber(domain.price);
};

const markDomainTaken = (items = [], soldDomain = "") => {
  const cleanSold = normalizeDomain(soldDomain);

  if (!cleanSold) return items;

  return items.map((item) =>
    normalizeDomain(item.name) === cleanSold
      ? {
          ...item,
          available: false,
          price: "Taken",
          rawPrice: 0,
          brokerFee: premiumTakenPrice,
          realtimeTaken: true,
        }
      : item
  );
};

/* ==============================
   RAZORPAY SCRIPT LOADER
============================== */
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const existingScript = document.querySelector(
      'script[src="https://checkout.razorpay.com/v1/checkout.js"]'
    );

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(true));
      existingScript.addEventListener("error", () => resolve(false));
      return;
    }

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);

    document.body.appendChild(script);
  });
};

function Domain() {
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [addingDomainName, setAddingDomainName] = useState("");
  const [results, setResults] = useState([]);
  const [activePanel, setActivePanel] = useState("search");
  const [cart, setCart] = useState([]);
  const [myDomains, setMyDomains] = useState([]);
  const [renewals, setRenewals] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState("");
  const [dnsRecords, setDnsRecords] = useState([defaultDnsRecord]);
  const [dnsLoading, setDnsLoading] = useState(false);
  const [savingDns, setSavingDns] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");
  const [realtimeStatus, setRealtimeStatus] = useState("connecting");
  const [favoriteDomains, setFavoriteDomains] = useState([]);

  const totalCartItems = useMemo(() => cart.length, [cart]);

  const cartTotalAmount = useMemo(() => {
    return cart.reduce((total, item) => total + getDomainPrice(item), 0);
  }, [cart]);

  const searchBaseName = useMemo(() => getBaseName(search), [search]);

  const exploreEndingCards = useMemo(() => {
    if (!searchBaseName) return [];

    return extensionCards.map((ext) => {
      const domainName = `${searchBaseName}${ext}`;
      const found = results.find(
        (item) => normalizeDomain(item.name) === domainName
      );

      if (found) return found;

      return {
        name: domainName,
        extension: ext,
        available: false,
        price: "Taken",
        rawPrice: 0,
        brokerFee: premiumTakenPrice,
      };
    });
  }, [results, searchBaseName]);

  const showMessage = useCallback((text, type = "success") => {
    setMessage(text || "");
    setMessageType(type);
  }, []);

  /* ==============================
     AUTH FETCH
  ============================== */
  const authFetch = useCallback(async (endpoint, options = {}) => {
    const token = getToken();

    if (!token) {
      clearAuth();
      throw new Error("Session expired. Please login again.");
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      cache: "no-store",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });

    const data = await response.json().catch(() => ({}));

    if (response.status === 401 || response.status === 403) {
      clearAuth();
      throw new Error("Session expired. Please login again.");
    }

    if (!response.ok || data?.success === false) {
      throw new Error(data?.message || "Request failed.");
    }

    return data;
  }, []);

  /* ==============================
     LOAD USER-SPECIFIC DOMAIN DATA
  ============================== */
  const loadInitialData = useCallback(
    async (silent = false) => {
      try {
        if (!silent) setInitialLoading(true);

        const [cartData, myData, renewalData] = await Promise.all([
          authFetch("/domains/cart"),
          authFetch("/domains/my-domains"),
          authFetch("/domains/renewals"),
        ]);

        setCart(Array.isArray(cartData?.data) ? cartData.data : []);
        setMyDomains(Array.isArray(myData?.data) ? myData.data : []);
        setRenewals(Array.isArray(renewalData?.data) ? renewalData.data : []);

        if (!silent) showMessage("");
      } catch (error) {
        setCart([]);
        setMyDomains([]);
        setRenewals([]);
        showMessage(error.message || "Failed to load domain data.", "error");
      } finally {
        if (!silent) setInitialLoading(false);
      }
    },
    [authFetch, showMessage]
  );

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  /* ==============================
     REALTIME SOCKET
  ============================== */
  useEffect(() => {
    const socket = connectRealtimeSocket();

    if (!socket) {
      setRealtimeStatus("offline");
      return undefined;
    }

    let refreshTimer = null;

    const softRefresh = () => {
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        loadInitialData(true);
      }, 400);
    };

    const handleConnected = () => {
      setRealtimeStatus("connected");
    };

    const handleDisconnected = () => {
      setRealtimeStatus("offline");
    };

    const handleGlobalAvailabilityUpdate = (payload = {}) => {
      const soldDomain = normalizeDomain(payload.domain);

      if (!soldDomain) return;

      setRealtimeStatus("connected");

      setResults((prev) => markDomainTaken(prev, soldDomain));

      setCart((prev) => {
        const existsInCart = prev.some(
          (item) => normalizeDomain(item.name) === soldDomain
        );

        if (existsInCart) {
          showMessage(
            `${soldDomain} is now taken and removed from your cart.`,
            "error"
          );
        }

        return prev.filter((item) => normalizeDomain(item.name) !== soldDomain);
      });

      softRefresh();
    };

    const handleDomainStatus = (payload = {}) => {
      setRealtimeStatus("connected");

      const purchasedDomains = Array.isArray(payload?.purchasedDomains)
        ? payload.purchasedDomains.map((item) => normalizeDomain(item)).filter(Boolean)
        : payload?.domain
        ? String(payload.domain)
            .split(",")
            .map((item) => normalizeDomain(item))
            .filter(Boolean)
        : [];

      if (purchasedDomains.length > 0) {
        setResults((prev) =>
          purchasedDomains.reduce(
            (updated, domainName) => markDomainTaken(updated, domainName),
            prev
          )
        );

        setCart((prev) =>
          prev.filter(
            (item) => !purchasedDomains.includes(normalizeDomain(item.name))
          )
        );
      }

      softRefresh();

      if (payload?.message) {
        showMessage(payload.message, "success");
      }
    };

    const handlePaymentStatus = (payload = {}) => {
      setRealtimeStatus("connected");

      if (payload?.status === "success") {
        showMessage(
          payload.message || "Payment completed successfully.",
          "success"
        );
      }
    };

    const handleDashboardUpdate = () => {
      setRealtimeStatus("connected");
      softRefresh();
    };

    const handleSocketAuthError = () => {
      clearAuth();
      window.location.href = "/signin";
    };

    socket.on("connect", handleConnected);
    socket.on("disconnect", handleDisconnected);
    socket.on("socket:connected", handleConnected);
    socket.on("domain:availability:update", handleGlobalAvailabilityUpdate);
    socket.on("domain:status", handleDomainStatus);
    socket.on("payment:status", handlePaymentStatus);
    socket.on("dashboard:update", handleDashboardUpdate);
    socket.on("notification:new", softRefresh);
    socket.on("activity:new", softRefresh);
    socket.on("connect_error", handleSocketAuthError);

    if (socket.connected) {
      handleConnected();
    }

    return () => {
      clearTimeout(refreshTimer);

      socket.off("connect", handleConnected);
      socket.off("disconnect", handleDisconnected);
      socket.off("socket:connected", handleConnected);
      socket.off("domain:availability:update", handleGlobalAvailabilityUpdate);
      socket.off("domain:status", handleDomainStatus);
      socket.off("payment:status", handlePaymentStatus);
      socket.off("dashboard:update", handleDashboardUpdate);
      socket.off("notification:new", softRefresh);
      socket.off("activity:new", softRefresh);
      socket.off("connect_error", handleSocketAuthError);

      disconnectRealtimeSocket();
    };
  }, [loadInitialData, showMessage]);

  /* ==============================
     DOMAIN SEARCH
  ============================== */
  const handleSearch = async () => {
    const domainName = normalizeDomain(search);

    if (!domainName) {
      showMessage("Please enter a domain name.", "error");
      return;
    }

    try {
      setLoading(true);
      setResults([]);
      setActivePanel("search");
      showMessage("");

      const data = await authFetch(
        `/domains/search?name=${encodeURIComponent(domainName)}`
      );

      setResults(Array.isArray(data?.data) ? data.data : []);
      showMessage(data?.message || "Search completed successfully.", "success");
    } catch (error) {
      setResults([]);
      showMessage(
        error.message || "Something went wrong while searching domains.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  /* ==============================
     ADD TO USER-SPECIFIC CART
  ============================== */
  const handleAddToCart = async (domain) => {
    const safeDomain = normalizeDomain(domain?.name);

    if (!safeDomain || domain?.available === false) return;

    try {
      setAddingDomainName(safeDomain);
      showMessage("");

      const payload = {
        name: safeDomain,
        extension: domain.extension || getDomainExtension(safeDomain),
        price: domain.price,
        rawPrice: getDomainPrice(domain),
        available: true,
      };

      const data = await authFetch("/domains/cart", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      setCart(Array.isArray(data?.data) ? data.data : []);
      showMessage(data?.message || `${safeDomain} added successfully.`, "success");
    } catch (error) {
      showMessage(error.message || "Unable to add domain.", "error");
    } finally {
      setAddingDomainName("");
    }
  };

  const handleRemoveFromCart = async (domainName) => {
    const safeDomain = normalizeDomain(domainName);

    if (!safeDomain) return;

    try {
      const data = await authFetch(
        `/domains/cart/${encodeURIComponent(safeDomain)}`,
        {
          method: "DELETE",
        }
      );

      setCart(Array.isArray(data?.data) ? data.data : []);
      showMessage(data?.message || `${safeDomain} removed from cart.`, "success");
    } catch (error) {
      showMessage(error.message || "Unable to remove domain.", "error");
    }
  };

  /* ==============================
     RAZORPAY DOMAIN CHECKOUT
  ============================== */
  const activateDomainsAfterPayment = useCallback(
    async ({ verifiedPayment }) => {
      const purchasedDomains = cart.map((item) => normalizeDomain(item.name));

      const data = await authFetch("/domains/mock-purchase", {
        method: "POST",
        body: JSON.stringify({
          paymentId: verifiedPayment?.payment?.paymentId || "",
          orderId: verifiedPayment?.payment?.orderId || "",
          paymentMode: verifiedPayment?.payment?.mode || "test",
          amount: cartTotalAmount,
          purchasedDomains,
        }),
      });

      setCart([]);
      setMyDomains(Array.isArray(data?.myDomains) ? data.myDomains : []);
      setRenewals(Array.isArray(data?.renewals) ? data.renewals : []);
      setResults((prev) =>
        purchasedDomains.reduce(
          (updated, domainName) => markDomainTaken(updated, domainName),
          prev
        )
      );

      return data;
    },
    [authFetch, cart, cartTotalAmount]
  );

  const handleDomainCheckout = useCallback(async () => {
    try {
      setCheckoutLoading(true);
      showMessage("");

      const token = getToken();

      if (!token) {
        clearAuth();
        showMessage("Session expired. Please login again.", "error");
        window.location.href = "/signin";
        return;
      }

      if (!cart.length) {
        showMessage("Cart is empty.", "error");
        return;
      }

      if (!cartTotalAmount || cartTotalAmount <= 0) {
        showMessage("Invalid cart amount.", "error");
        return;
      }

      const scriptLoaded = await loadRazorpayScript();

      if (!scriptLoaded) {
        throw new Error("Payment gateway failed to load. Please try again.");
      }

      const domainNames = cart.map((item) => normalizeDomain(item.name));
      const productName =
        domainNames.length === 1
          ? domainNames[0]
          : `Domain Purchase - ${domainNames.length} domains`;

      const productPayload = {
        productType: "domain",
        productName,
        planName: productName,
        domainName: domainNames.join(","),
        extension: domainNames.map(getDomainExtension).join(","),
        amount: cartTotalAmount,
      };

      const orderData = await authFetch("/payments/create-order", {
        method: "POST",
        body: JSON.stringify(productPayload),
      });

      if (!orderData?.order?.id || !orderData?.key) {
        throw new Error("Invalid payment order response.");
      }

      const storedUser = getStoredUser();

      const razorpayOptions = {
        key: orderData.key,
        amount: orderData.order.amount,
        currency: orderData.order.currency || "INR",
        name: "DoHostingPay",
        description: productName,
        order_id: orderData.order.id,

        prefill: {
          name: storedUser?.fullName || storedUser?.name || "",
          email: storedUser?.email || "",
          contact: storedUser?.phone || "",
        },

        notes: {
          productType: "domain",
          totalDomains: String(domainNames.length),
        },

        theme: {
          color: "#6d28d9",
        },

        modal: {
          confirm_close: true,
          ondismiss: () => {
            setCheckoutLoading(false);
            showMessage(
              "Payment popup closed. Domain purchase was not completed.",
              "error"
            );
          },
        },

        handler: async (response) => {
          try {
            showMessage("Payment received. Verifying securely...", "success");

            const verifiedPayment = await authFetch("/payments/verify", {
              method: "POST",
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                ...productPayload,
              }),
            });

            await activateDomainsAfterPayment({ verifiedPayment });

            await loadInitialData(true);

            showMessage(
              "Domain payment successful. Your domains are now active.",
              "success"
            );

            setActivePanel("mydomains");
          } catch (error) {
            showMessage(
              error.message ||
                "Payment verification completed but domain activation failed.",
              "error"
            );
          } finally {
            setCheckoutLoading(false);
          }
        },
      };

      const razorpay = new window.Razorpay(razorpayOptions);

      razorpay.on("payment.failed", () => {
        setCheckoutLoading(false);
        showMessage("Payment failed. Please try again.", "error");
      });

      razorpay.open();
    } catch (error) {
      showMessage(error.message || "Unable to start domain payment.", "error");
      setCheckoutLoading(false);
    }
  }, [
    activateDomainsAfterPayment,
    authFetch,
    cart,
    cartTotalAmount,
    loadInitialData,
    showMessage,
  ]);

  /* ==============================
     FAVORITE / BROKER
  ============================== */
  const handleFavoriteToggle = (domainName) => {
    const safeDomain = normalizeDomain(domainName);

    if (!safeDomain) return;

    setFavoriteDomains((prev) =>
      prev.includes(safeDomain)
        ? prev.filter((item) => item !== safeDomain)
        : [safeDomain, ...prev]
    );
  };

  const handleBrokerRequest = (domainName) => {
    showMessage(
      `${domainName} is taken. Broker request feature will connect this domain to support/sales.`,
      "success"
    );
  };

  /* ==============================
     DNS LOAD / SAVE
  ============================== */
  const handleLoadDns = async (domainName) => {
    const safeDomain = normalizeDomain(domainName);

    if (!safeDomain) {
      showMessage("Please select a domain first.", "error");
      return;
    }

    try {
      setSelectedDomain(safeDomain);
      setActivePanel("dns");
      setDnsLoading(true);
      showMessage("");

      const data = await authFetch(`/domains/dns/${encodeURIComponent(safeDomain)}`);

      setDnsRecords(
        Array.isArray(data?.data) && data.data.length
          ? data.data
          : [defaultDnsRecord]
      );

      showMessage(data?.message || "DNS records loaded successfully.", "success");
    } catch (error) {
      setDnsRecords([defaultDnsRecord]);
      showMessage(error.message || "Failed to load DNS records.", "error");
    } finally {
      setDnsLoading(false);
    }
  };

  const handleDnsChange = (index, field, value) => {
    setDnsRecords((prev) =>
      prev.map((record, currentIndex) =>
        currentIndex === index ? { ...record, [field]: value } : record
      )
    );
  };

  const addDnsRow = () => {
    setDnsRecords((prev) => [
      ...prev,
      {
        type: "A",
        host: "",
        value: "",
        ttl: "3600",
        priority: 0,
      },
    ]);
  };

  const removeDnsRow = (index) => {
    setDnsRecords((prev) => {
      const updated = prev.filter((_, currentIndex) => currentIndex !== index);
      return updated.length ? updated : [defaultDnsRecord];
    });
  };

  const validateDnsRecords = () => {
    for (const record of dnsRecords) {
      if (!record.type || !record.host || !record.value || !record.ttl) {
        return "DNS type, host, value and TTL are required.";
      }

      if (Number(record.ttl || 0) <= 0) {
        return "TTL must be greater than 0.";
      }
    }

    return "";
  };

  const handleSaveDns = async () => {
    if (!selectedDomain) {
      showMessage("Please select a domain first.", "error");
      return;
    }

    const validationError = validateDnsRecords();

    if (validationError) {
      showMessage(validationError, "error");
      return;
    }

    try {
      setSavingDns(true);

      const payload = {
        domain: selectedDomain,
        records: dnsRecords.map((record) => ({
          type: String(record.type || "").trim(),
          host: String(record.host || "").trim(),
          value: String(record.value || "").trim(),
          ttl: String(record.ttl || "3600").trim(),
          priority: Number(record.priority || 0),
        })),
      };

      const data = await authFetch("/domains/dns/update", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      showMessage(data?.message || "DNS records updated successfully.", "success");
    } catch (error) {
      showMessage(error.message || "Failed to save DNS records.", "error");
    } finally {
      setSavingDns(false);
    }
  };

  /* ==============================
     RENEWAL
  ============================== */
  const handleToggleAutoRenew = async (domainName, nextValue) => {
    const safeDomain = normalizeDomain(domainName);

    if (!safeDomain) return;

    try {
      const data = await authFetch(
        `/domains/renewals/${encodeURIComponent(safeDomain)}/auto-renew`,
        {
          method: "PATCH",
          body: JSON.stringify({ autoRenew: nextValue }),
        }
      );

      setRenewals((prev) =>
        prev.map((item) =>
          normalizeDomain(item.name) === safeDomain
            ? { ...item, autoRenew: nextValue }
            : item
        )
      );

      setMyDomains((prev) =>
        prev.map((item) =>
          normalizeDomain(item.name) === safeDomain
            ? { ...item, autoRenew: nextValue }
            : item
        )
      );

      showMessage(data?.message || "Auto renew updated successfully.", "success");
    } catch (error) {
      showMessage(error.message || "Failed to update auto renew.", "error");
    }
  };

  /* ==============================
     DOMAIN MARKET CARD
  ============================== */
  const renderDomainMarketCard = (item) => {
    const safeName = normalizeDomain(item.name);
    const isFavorite = favoriteDomains.includes(safeName);
    const isAdding = addingDomainName === safeName;

    return (
      <div
        className={`domain-market-card ${
          item.available ? "domain-market-available" : "domain-market-taken"
        }`}
        key={item.name}
      >
        <button
          type="button"
          className={`domain-heart-btn ${isFavorite ? "active" : ""}`}
          onClick={() => handleFavoriteToggle(item.name)}
          aria-label="Save domain"
        >
          {isFavorite ? "♥" : "♡"}
        </button>

        {item.available ? (
          <>
            <span className="domain-available-label">Available</span>

            <h3>{item.name}</h3>

            <div className="domain-price-line">
              <span>{item.price}</span>
              <small>1st year offer</small>
            </div>

            <button
              type="button"
              className="domain-cart-icon-btn"
              onClick={() => handleAddToCart(item)}
              disabled={isAdding || checkoutLoading}
            >
              {isAdding ? "..." : "🛒"}
            </button>
          </>
        ) : (
          <>
            <span className="domain-taken-label">Domain Taken</span>

            <h3 className="domain-muted-name">⊘ {item.name}</h3>

            <small>Broker Service Fee</small>

            <strong className="domain-broker-price">
              {item.brokerFee || premiumTakenPrice}
            </strong>

            <button
              type="button"
              className="domain-cart-icon-btn"
              onClick={() => handleBrokerRequest(item.name)}
            >
              🛒
            </button>
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <Navbar />

      <section className="common-page-section">
        <div className="common-page-container">
          <div className="domain-hero-block">
            <span className="common-page-badge">Domains</span>

            <h1>Search &amp; Manage Domains</h1>

            <p>
              Find the perfect domain name for your business and manage
              everything from one secure user-specific panel.
            </p>

            <div className="domain-realtime-chip">
              <span
                className={
                  realtimeStatus === "connected"
                    ? "domain-realtime-dot online"
                    : "domain-realtime-dot offline"
                }
              />

              <span>
                {realtimeStatus === "connected"
                  ? "Realtime connected"
                  : realtimeStatus === "connecting"
                  ? "Connecting realtime..."
                  : "Realtime offline"}
              </span>
            </div>

            <div className="domain-search-box">
              <input
                type="text"
                placeholder="Enter domain name, example yahoo"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") handleSearch();
                }}
                autoComplete="off"
              />

              <button type="button" onClick={handleSearch} disabled={loading}>
                {loading ? "Searching..." : "Search"}
              </button>
            </div>

            {message ? (
              <output className={`domain-message ${messageType}`}>
                {message}
              </output>
            ) : null}
          </div>

          {searchBaseName && (results.length > 0 || !loading) ? (
            <div className="domain-explore-section">
              <div className="domain-explore-head">
                <h2>🌐 Explore {searchBaseName} Endings</h2>

                <button type="button" onClick={handleSearch}>
                  See All
                </button>
              </div>

              <div className="domain-market-grid">
                {exploreEndingCards.map(renderDomainMarketCard)}
              </div>
            </div>
          ) : null}

          <div className="domain-dashboard-top">
            <div className="domain-cart-box">
              <div className="domain-cart-head">
                <div>
                  <h3>Cart ({totalCartItems})</h3>

                  <p>Selected domains ready for secure Razorpay checkout.</p>

                  {cart.length > 0 ? (
                    <strong>Total: ₹{cartTotalAmount}</strong>
                  ) : null}
                </div>

                {cart.length > 0 ? (
                  <button
                    type="button"
                    className="buy-btn"
                    onClick={handleDomainCheckout}
                    disabled={checkoutLoading}
                  >
                    {checkoutLoading ? "Processing..." : "Pay & Buy Now"}
                  </button>
                ) : null}
              </div>

              {cart.length === 0 ? (
                <div className="domain-empty-state">
                  <p>No domain added yet.</p>
                </div>
              ) : (
                <div className="domain-mini-cart">
                  {cart.map((item) => (
                    <div className="domain-mini-cart-row" key={item.name}>
                      <div className="domain-mini-cart-info">
                        <span>{item.name}</span>
                        <small>{item.price || "Ready to purchase"}</small>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleRemoveFromCart(item.name)}
                        disabled={checkoutLoading}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {activePanel === "search" && results.length > 0 ? (
            <div className="domain-results">
              {results.map((item) => {
                const safeName = normalizeDomain(item.name);
                const isAdding = addingDomainName === safeName;

                return (
                  <div className="domain-result-card" key={item.name}>
                    <div className="domain-info">
                      <h3>{item.name}</h3>

                      <p className={item.available ? "available" : "taken"}>
                        {item.available ? "Available" : "Already Taken"}
                      </p>
                    </div>

                    <div className="domain-action">
                      {item.available ? (
                        <>
                          <span className="price">{item.price}</span>

                          <button
                            type="button"
                            className="buy-btn"
                            onClick={() => handleAddToCart(item)}
                            disabled={isAdding || checkoutLoading}
                          >
                            {isAdding ? "Adding..." : "Add"}
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="disabled-btn"
                          onClick={() => handleBrokerRequest(item.name)}
                        >
                          Broker Request
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}

          <div className="common-page-card-grid domain-service-grid">
            <button
              type="button"
              className={`common-page-card ${
                activePanel === "mydomains" ? "domain-active-card" : ""
              }`}
              onClick={() => setActivePanel("mydomains")}
            >
              <h3>My Domains</h3>

              <p>Manage all your purchased domains easily.</p>
            </button>

            <button
              type="button"
              className={`common-page-card ${
                activePanel === "renewals" ? "domain-active-card" : ""
              }`}
              onClick={() => setActivePanel("renewals")}
            >
              <h3>Renewals</h3>

              <p>Never lose your domain with auto-renewals.</p>
            </button>

            <button
              type="button"
              className={`common-page-card ${
                activePanel === "dns" ? "domain-active-card" : ""
              }`}
              onClick={() => setActivePanel("dns")}
            >
              <h3>DNS Settings</h3>

              <p>Control DNS, hosting, and redirection settings.</p>
            </button>
          </div>

          {initialLoading ? <p>Loading domain data...</p> : null}

          {activePanel === "mydomains" ? (
            <div className="domain-panel">
              <h2>My Domains</h2>

              {myDomains.length === 0 ? (
                <p>No domains purchased yet.</p>
              ) : (
                <div className="domain-table-wrap">
                  <table className="domain-table">
                    <thead>
                      <tr>
                        <th>Domain</th>
                        <th>Status</th>
                        <th>Expiry</th>
                        <th>Auto Renew</th>
                        <th>DNS</th>
                      </tr>
                    </thead>

                    <tbody>
                      {myDomains.map((domain) => (
                        <tr key={domain.name}>
                          <td>{domain.name}</td>
                          <td>{domain.status || "active"}</td>
                          <td>{domain.expiryDate || "-"}</td>
                          <td>{domain.autoRenew ? "Enabled" : "Disabled"}</td>
                          <td>
                            <button
                              type="button"
                              className="small-btn"
                              onClick={() => handleLoadDns(domain.name)}
                            >
                              Manage DNS
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : null}

          {activePanel === "renewals" ? (
            <div className="domain-panel">
              <h2>Renewals</h2>

              {renewals.length === 0 ? (
                <p>No renewals available.</p>
              ) : (
                <div className="domain-table-wrap">
                  <table className="domain-table">
                    <thead>
                      <tr>
                        <th>Domain</th>
                        <th>Expiry</th>
                        <th>Auto Renew</th>
                        <th>Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {renewals.map((item) => (
                        <tr key={item.name}>
                          <td>{item.name}</td>
                          <td>{item.expiryDate || "-"}</td>
                          <td>{item.autoRenew ? "Enabled" : "Disabled"}</td>
                          <td>
                            <button
                              type="button"
                              className="small-btn"
                              onClick={() =>
                                handleToggleAutoRenew(item.name, !item.autoRenew)
                              }
                            >
                              {item.autoRenew ? "Disable" : "Enable"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : null}

          {activePanel === "dns" ? (
            <div className="domain-panel">
              <h2>DNS Settings</h2>

              <div className="dns-domain-picker">
                <select
                  value={selectedDomain}
                  onChange={(event) => handleLoadDns(event.target.value)}
                  aria-label="Select domain for DNS"
                >
                  <option value="">Select domain</option>

                  {myDomains.map((domain) => (
                    <option key={domain.name} value={domain.name}>
                      {domain.name}
                    </option>
                  ))}
                </select>
              </div>

              {dnsLoading ? (
                <p>Loading DNS records...</p>
              ) : (
                <>
                  <div className="dns-records-wrap">
                    {dnsRecords.map((record, index) => (
                      <div
                        className="dns-record-row"
                        key={`${record.type}-${record.host}-${index}`}
                      >
                        <select
                          value={record.type}
                          onChange={(event) =>
                            handleDnsChange(index, "type", event.target.value)
                          }
                          aria-label="DNS record type"
                        >
                          <option value="A">A</option>
                          <option value="AAAA">AAAA</option>
                          <option value="CNAME">CNAME</option>
                          <option value="MX">MX</option>
                          <option value="TXT">TXT</option>
                        </select>

                        <input
                          type="text"
                          placeholder="Host"
                          value={record.host}
                          onChange={(event) =>
                            handleDnsChange(index, "host", event.target.value)
                          }
                        />

                        <input
                          type="text"
                          placeholder="Value"
                          value={record.value}
                          onChange={(event) =>
                            handleDnsChange(index, "value", event.target.value)
                          }
                        />

                        <input
                          type="text"
                          placeholder="TTL"
                          value={record.ttl}
                          onChange={(event) =>
                            handleDnsChange(index, "ttl", event.target.value)
                          }
                        />

                        <input
                          type="number"
                          placeholder="Priority"
                          value={record.priority || 0}
                          onChange={(event) =>
                            handleDnsChange(index, "priority", event.target.value)
                          }
                        />

                        <button
                          type="button"
                          className="remove-btn"
                          onClick={() => removeDnsRow(index)}
                        >
                          X
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="dns-actions">
                    <button
                      type="button"
                      className="small-btn"
                      onClick={addDnsRow}
                    >
                      Add Record
                    </button>

                    <button
                      type="button"
                      className="buy-btn"
                      onClick={handleSaveDns}
                      disabled={savingDns}
                    >
                      {savingDns ? "Saving..." : "Save DNS"}
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : null}
        </div>
      </section>
    </>
  );
}

export default Domain;